import React, { useState, useEffect, useRef } from "react";
import {
  bootstrapCameraKit,
  createMediaStreamSource,
  Transform2D,
} from "@snap/camera-kit";
import EmblaCarousel from "./EmblaCarousel";
import Snaplogo from "../../Assets/Camera/Powered_bysnap.png";
import { CameraControls, Navbar, PrivacyPopup } from "./UI"; // Import moved components
import VantaBackground from "./VantaBackground";
import Preloader from "./Preloader";
import "./Camera.css";
import { Download, Share2, ArrowLeft } from "lucide-react"; // Import icons
import Sparkle from "../../Assets/Camera/Sparkle.svg";
import { VideoProcessor } from "./videoProcessor";
import { Settings } from "./settings";
import { MediaRecorderManager } from "./recorder";
import ShotWatermark from "../../Assets/Camera/SHOT.png";

const videoProcessor = new VideoProcessor();
const mediaRecorder = new MediaRecorderManager(videoProcessor);

// --- CameraKit Lens Audio Monitor (from CameraKit-Record-Template) ---
if (!window.global) window.global = {};
if (!global.audioContexts) global.audioContexts = [];
if (!global.monitorNodes) global.monitorNodes = [];

function setupAudioContextMonitor() {
  const originalAudioContext = window.AudioContext || window.webkitAudioContext;
  let capturedAudioContext = null;

  window.AudioContext = window.webkitAudioContext = function () {
    capturedAudioContext = new originalAudioContext();
    // console.log("Audio context created:", capturedAudioContext);
    global.audioContexts.push(capturedAudioContext);
    return capturedAudioContext;
  };
}

function setupAudioNodeMonitor() {
  const originalConnect = AudioNode.prototype.connect;
  AudioNode.prototype.connect = function (destinationNode) {
    for (let i = 0; i < global.audioContexts.length; i++) {
      if (this.context === global.audioContexts[i]) {
        // console.log("Context id: ", i);
      }
    }
    // if the current node is a gainNode, create another stream node and connect it
    if (
      destinationNode instanceof
      (window.AudioDestinationNode ||
        (window.AudioContext &&
          window.AudioContext.prototype.destination &&
          window.AudioContext.prototype.destination.constructor))
    ) {
      // create monitor node
      let streamNode = this.context.createMediaStreamDestination();
      global.monitorNodes.push(streamNode);
      // connect current node to the monitor node
      this.connect(streamNode);
    }
    return originalConnect.apply(this, arguments);
  };
}

if (!window._lensAudioMonitorSetup) {
  setupAudioContextMonitor();
  setupAudioNodeMonitor();
  window._lensAudioMonitorSetup = true;
}

const LensCarousel = ({
  lenses,
  onLensChange,
  onRecord,
  activeIndex,
  isRecording,
  setActiveIndex,
  loadedLensIndex,
  fadeOut, // Add fadeOut prop
}) => {
  const [recordingProgress, setRecordingProgress] = useState(0);
  const recordingAnimationRef = useRef(null);
  const circleRadius = 50; // Radius of the circle
  const circumference = 2 * Math.PI * circleRadius; // Circumference of the circle
  const [showPopup, setShowPopup] = useState(true); // State to manage the popup visibility
  const [fadeOutPopup, setFadeOutPopup] = useState(false); // State to trigger fade-out animation
  const [elapsedTime, setElapsedTime] = useState(0); // Timer state
  const [timerFade, setTimerFade] = useState("");

  useEffect(() => {
    let timerInterval;
    if (isRecording) {
      const startTime = Date.now() - elapsedTime * 1000;
      timerInterval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 250);
    } else {
      setElapsedTime(0); // Reset timer when not recording
    }
    return () => clearInterval(timerInterval);
  }, [isRecording]);

  useEffect(() => {
    if (isRecording) {
      const startTime = Date.now();
      recordingAnimationRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = (elapsed % 5000) / 5000; // Complete one full circle every 5 seconds
        setRecordingProgress(progress);
      }, 50);
    } else {
      if (recordingAnimationRef.current) {
        clearInterval(recordingAnimationRef.current);
      }
      setRecordingProgress(0); // Reset progress when recording stops
    }

    return () => {
      if (recordingAnimationRef.current) {
        clearInterval(recordingAnimationRef.current);
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (isRecording) {
      setTimerFade("fade-timer-in");
    } else if (elapsedTime > 0) {
      setTimerFade("fade-timer-out");
      const timeout = setTimeout(() => setTimerFade(""), 400);
      return () => clearTimeout(timeout);
    } else {
      setTimerFade("");
    }
  }, [isRecording, elapsedTime]);

  const handleSelect = (index) => {
    if (!isRecording) {
      setActiveIndex(index);
      onLensChange(lenses[index]);
    }
  };

  let holdTimeout;

  const handlePointerDown = () => {
    // Start a timeout for hold detection
    holdTimeout = setTimeout(() => {
      onRecord(true); // Trigger hold behavior (start recording)
    }, 500); // 500ms hold duration
  };

  const handlePointerUp = () => {
    // Clear the hold timeout
    clearTimeout(holdTimeout);

    // Do nothing on release; recording stops only on a second tap
  };

  const handlePointerClick = () => {
    // Handle single tap behavior
    if (isRecording) {
      onRecord(false); // Stop recording on a second tap
    } else {
      onRecord(); // Log "Photo taken" on a single tap
    }
  };

  const handlePointerLeave = () => {
    // Clear the hold timeout if the pointer leaves the button
    clearTimeout(holdTimeout);
  };

  const handleClosePopup = () => {
    setFadeOutPopup(true); // Trigger fade-out animation
    setTimeout(() => setShowPopup(false), 300); // Remove popup after animation
  };

  // Helper to format seconds as mm:ss
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="lens-carousel-wrapper" style={{ position: "relative" }}>
      {showPopup && (
        <div
          className={`popup-container ${fadeOutPopup ? "fade-out" : ""}`}
          style={{
            position: "absolute",
            top: "-80px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "opacity 0.3s ease", // Add transition for fade-out
            opacity: fadeOutPopup ? 0 : 1, // Control opacity for fade-out
          }}
        >
          <span>Tap to click a photo, tap and hold to record a video</span>
          <button
            onClick={handleClosePopup}
            style={{
              marginLeft: "10px",
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            ✕
          </button>
        </div>
      )}
      {/* Only the carousel is masked, not the instructions/timer */}
      <div className="carousel-mask">
        <EmblaCarousel
          slides={lenses}
          options={{ loop: true }}
          onSelect={handleSelect}
          activeIndex={loadedLensIndex}
          isRecording={isRecording}
          recordingFadeOut={!isRecording && fadeOut} // Pass fadeOut to EmblaCarousel
        />
      </div>
      {/* Timer above record button with fade animation */}
      {(isRecording || timerFade === "fade-timer-out") && (
        <div
          className={timerFade}
          style={{
            position: "absolute",
            top: "-10%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            zIndex: 2,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            padding: "6px 16px",
            borderRadius: "20px",
            fontSize: "1.2rem",
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {formatTime(elapsedTime)}
        </div>
      )}
      {/* Record Button Only (SVG removed) */}
      <div
        style={{
          position: "absolute",
          top: "38%",
          left: "49.9%",
          transform: "translate(-50%, -50%)",
          zIndex: 0,
          pointerEvents: "auto",
        }}
      >
        <button
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onClick={handlePointerClick}
          onPointerLeave={handlePointerLeave}
          className="record-button"
          style={{
            width: "110px",
            height: "110px",
            borderRadius: "50%",
            backgroundColor: "white",
            border: "2px solid black",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            position: "relative",
            opacity: 0,
            pointerEvents: "auto",
          }}
        >
          {isRecording ? "Stop" : "Rec"}
        </button>
      </div>
    </div>
  );
};

const Camera = () => {
  const [isMicOn, setIsMicOn] = useState(true); // State to track microphone status
  const [currentLenses, setCurrentLenses] = useState([]);
  const [isBackFacing, setIsBackFacing] = useState(false); // Initialize as false
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadedLensIndex, setLoadedLensIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false); // State to track recording status
  const [hideUI, setHideUI] = useState(false); // State to hide UI during recording
  const [fadeOut, setFadeOut] = useState(false); // State to trigger fade-out animation
  const [fadeIn, setFadeIn] = useState(false); // State to trigger fade-in animation
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(true); // State to show privacy popup
  const [permissionsGranted, setPermissionsGranted] = useState(false); // State to track user agreement
  const [vantaFadeOut, setVantaFadeOut] = useState(false); // State to trigger Vanta fade-out
  const [previewPopup, setPreviewPopup] = useState(null); // State to manage preview popup
  const [isVideoPreview, setIsVideoPreview] = useState(false); // State to differentiate between image and video preview

  const canvasRef = useRef(null);
  const sessionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null); // Ref to store the MediaRecorder instance
  const handleResize = () => {
    updateRenderSize(sessionRef.current.source, canvasRef.current);
  };
  // Check if the device is mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const getConstraints = () => {
    return isMobile
      ? isBackFacing
        ? Settings.camera.constraints.back
        : Settings.camera.constraints.front
      : Settings.camera.constraints.desktop;
  };

  useEffect(() => {
    const initCamera = async () => {
      if (showPrivacyPopup) {
        console.warn("Delaying camera initialization until user agrees.");
        return; // Do not initialize the camera until the user agrees
      }

      const cameraKit = await bootstrapCameraKit({
        apiToken: process.env.REACT_APP_API,
      });
      const session = await cameraKit.createSession({
        liveRenderTarget: canvasRef.current,
        renderOptions: {
          quality: "high",
          antialiasing: true,
        },
      });
      sessionRef.current = session;
      await loadCameraStream(session, isBackFacing);
      const { lenses } = await cameraKit.lensRepository.loadLensGroups([
        process.env.REACT_APP_GRPID,
      ]);
      setCurrentLenses(lenses);

      // Set FPS limit
      await session.setFPSLimit(60);

      // Update render size
      updateRenderSize(session.source, canvasRef.current, 1); // Increase source size by a factor of 2

      // Automatically apply the first lens when lenses are loaded
      if (lenses.length > 0) {
        handleLensChange(lenses[0]);
      }
    };

    initCamera();

    // Add resize listener
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);

      // Cleanup media stream on unmount
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [showPrivacyPopup]); // Add showPrivacyPopup as a dependency

  useEffect(() => {
    if (!showPrivacyPopup) {
      const timeout = setTimeout(() => {
        setShowPrivacyPopup(false);
      }, 1000); // Delay of 1000ms

      return () => clearTimeout(timeout); // Cleanup timeout on unmount
    }
  }, [showPrivacyPopup]);

  useEffect(() => {
    const handleHamburgerClick = () => {
      const hamburger = document.getElementById("hamburger");
      const mobileMenu = document.getElementById("mobile-menu");

      hamburger.classList.toggle("active");
      mobileMenu.classList.toggle("active");

      if (mobileMenu.classList.contains("active")) {
        document.body.style.overflow = "hidden";
        setHideUI(false); // Show UI when the hamburger menu is active
      } else {
        document.body.style.overflow = "auto";
        setHideUI(true); // Hide UI when the hamburger menu is inactive
      }
    };

    const hamburger = document.getElementById("hamburger");
    if (hamburger) {
      hamburger.addEventListener("click", handleHamburgerClick);
    }

    return () => {
      if (hamburger) {
        hamburger.removeEventListener("click", handleHamburgerClick);
      }
    };
  }, []);

  useEffect(() => {
    const snapLogo = document.querySelector(".snap-logo");
    if (snapLogo) {
      if (isRecording) {
        snapLogo.classList.add("fade-out");
        snapLogo.classList.remove("fade-in");
      } else {
        snapLogo.classList.add("fade-in");
        snapLogo.classList.remove("fade-out");
      }
    }
  }, [isRecording]);

  useEffect(() => {
    mediaRecorder.onRecordingComplete = async (fileUrl) => {
      console.log("Processing video for preview...");
      let retries = 5; // Retry up to 5 times
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      while (retries > 0) {
        const video = document.createElement("video");
        video.src = fileUrl;

        // Wait until the video metadata is loaded
        const metadataLoaded = await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            console.log("Video metadata loaded. Duration:", video.duration);
            resolve(video.duration > 0 && video.duration !== Infinity);
          };
          video.onerror = () => {
            console.error("Error loading video for preview.");
            resolve(false);
          };
        });

        if (metadataLoaded) {
          setPreviewPopup(fileUrl); // Load the video into the preview panel
          setIsVideoPreview(true); // Set to video preview
          return;
        }

        console.warn("Video not ready, retrying...");
        retries--;
        await delay(1000); // Wait 1 second before retrying
      }

      console.error("Failed to load video metadata after retries.");
    };
  }, []);

  const updateRenderSize = (source, liveRenderTarget) => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const width1 = Math.floor(window.innerWidth / 2) * 2;
    const height1 = Math.floor(window.innerHeight / 2) * 2;

    liveRenderTarget.style.width = `${width}px`;
    liveRenderTarget.style.height = `${height}px`;
    source.setRenderSize(width * 2, height * 2);

    // Update FFmpeg settings dynamically to match screen dimensions
    Settings.ffmpeg.outputOptions = [
      "-vf",
      `scale=${width1}:${height1}`,
      ...Settings.ffmpeg.outputOptions.filter(
        (opt) => !opt.startsWith("-vf") && !opt.startsWith("scale=")
      ),
    ];
  };

  const loadCameraStream = async (session, backFacing) => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      session.pause();
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: backFacing ? "environment" : "user",
        },
        audio: true, // Ensure audio is requested
      });
      const source = createMediaStreamSource(stream, {
        cameraType: backFacing ? "back" : "front",
      });
      mediaStreamRef.current = stream;
      await session.setSource(source);
      if (!backFacing) {
        source.setTransform(Transform2D.MirrorX);
      }
      session.play();
    } catch (error) {
      console.error("Error accessing media devices:", error);
    }
  };

  const flipCamera = () => {
    setIsBackFacing((prev) => !prev);
    loadCameraStream(sessionRef.current, !isBackFacing).then(() => {
      updateRenderSize(sessionRef.current.source, canvasRef.current); // Update the render size after flipping the camera
    });
  };

  const handleLensChange = async (lens) => {
    if (sessionRef.current) {
      try {
        console.log("Applying lens:", lens.name); // Debugging log
        await sessionRef.current.applyLens(lens); // Apply the selected lens
        // Set loadedLensIndex to the index of the lens that was just loaded
        const idx = currentLenses.findIndex((l) => l.id === lens.id);
        if (idx !== -1) setLoadedLensIndex(idx);
      } catch (error) {
        console.error("Error applying lens:", error);
      }
    }
  };

  const handlePhotoCapture = () => {
    if (canvasRef.current) {
      const mainCanvas = canvasRef.current;
      // Create a temporary canvas
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = mainCanvas.width;
      tempCanvas.height = mainCanvas.height;
      const ctx = tempCanvas.getContext("2d");
      // Draw the current frame from the main canvas
      ctx.drawImage(mainCanvas, 0, 0);
      // Draw the watermark
      const watermark = new window.Image();
      watermark.src = ShotWatermark;
      watermark.onload = () => {
        // Use a larger watermark on mobile
        const scale = isMobile ? 0.5 : 0.18;
        const margin = 0.05;
        const w = tempCanvas.width * scale;
        const h = (watermark.height / watermark.width) * w;
        // Center horizontally, margin from bottom
        const x = (tempCanvas.width - w) / 2;
        const y = tempCanvas.height - h - tempCanvas.height * margin;
        ctx.drawImage(watermark, x, y, w, h);
        const dataUrl = tempCanvas.toDataURL("image/png");
        setPreviewPopup(dataUrl); // Set the captured image for preview
        setIsVideoPreview(false); // Set to image preview
      };
    }
  };

  const handleVideoPreview = (videoUrl) => {
    setPreviewPopup(videoUrl); // Set the recorded video for preview
    setIsVideoPreview(true); // Set to video preview
  };

  const closePreviewPopup = () => {
    const previewElement = document.querySelector(".preview-popup");
    if (previewElement) {
      previewElement.classList.add("fade-out");
      setTimeout(() => {
        setPreviewPopup(null); // Close the preview popup
        setIsVideoPreview(false); // Reset to default
      }, 300); // Match the fade-out duration in CSS
    }
  };

  const downloadPreview = async () => {
    if (!previewPopup) return;

    try {
      // Fetch the recorded blob
      const response = await fetch(previewPopup);
      const blob = await response.blob();

      // Determine file type and name
      const isVideo = isVideoPreview;
      const mimeType = isVideo ? "video/mp4" : "image/png";
      const extension = isVideo ? "mp4" : "png";
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours()
      ).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(
        now.getSeconds()
      ).padStart(2, "0")}`;

      // Wrap blob in a File so the extension and MIME are honored
      const file = new File([blob], `augment.ing_${timestamp}.${extension}`, {
        type: mimeType,
      });

      // Create a new object URL and trigger download
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const sharePreview = async () => {
    if (navigator.share && previewPopup) {
      try {
        const response = await fetch(previewPopup);
        const blob = await response.blob();
        const fileType = isVideoPreview ? "video/mp4" : "image/png";
        const fileName = isVideoPreview ? "video.mp4" : "image.png";
        const file = new File([blob], fileName, { type: fileType });

        await navigator.share({
          files: [file],
          title: "Captured Media",
          text: "I just captured this on Augment.ing!",
        });
      } catch (error) {
        console.error("Error sharing media:", error);
      }
    } else {
      alert("Sharing is not supported on this device.");
    }
  };

  const handleRecord = async (isHold) => {
    if (isHold) {
      // Start recording on hold
      if (!isRecording) {
        setIsRecording(true);
        setFadeOut(true); // Trigger fade-out animation
        setFadeIn(false); // Reset fade-in animation
        setTimeout(() => setHideUI(true), 300); // Hide UI after fade-out animation
        console.log("Recording started");

        try {
          // Start recording using mediaRecorder
          await mediaRecorder.startRecording(
            canvasRef.current,
            getConstraints(),
            isMicOn
          );
          console.log("MediaRecorder started");
        } catch (error) {
          console.error("Failed to start recording:", error);
          setIsRecording(false); // Reset recording state if an error occurs
          setFadeOut(false); // Cancel fade-out animation
          setHideUI(false); // Show UI if recording fails
        }
      }
    } else {
      if (isRecording) {
        // Stop recording on a second tap
        setIsRecording(false);
        setFadeOut(false); // Cancel fade-out animation
        setTimeout(() => {
          setHideUI(false); // Show UI after fade-in animation
          setFadeIn(true); // Trigger fade-in animation
        }, 300);
        console.log("Recording stopped");
        const sparkleElement = document.createElement("img");
        sparkleElement.src = Sparkle;
        sparkleElement.className = "sparkle-center scale-up";
        document.body.appendChild(sparkleElement);

        const blurBackground = document.createElement("div");
        blurBackground.className = "blur-background";
        document.body.appendChild(blurBackground);

        const videoProcessingText = document.createElement("div");
        videoProcessingText.className = "video-processing-text";
        videoProcessingText.textContent = "Video Processing";
        document.body.appendChild(videoProcessingText);

        const removeText = () => {
          videoProcessingText.classList.add("fade-out");
          setTimeout(() => {
            document.body.removeChild(videoProcessingText);
          }, 300);
        };

        const videoPreviewCheck = setInterval(() => {
          const videoPreview = document.querySelector(".preview-popup");
          if (videoPreview) {
            sparkleElement.classList.add("scale-down");
            blurBackground.classList.add("fade-out");
            removeText();
            setTimeout(() => {
              document.body.removeChild(sparkleElement);
              document.body.removeChild(blurBackground);
            }, 300);
            clearInterval(videoPreviewCheck);
          }
        }, 100);

        try {
          // Stop recording and let the callback handle the video preview
          mediaRecorder.stopRecording();
          console.log("MediaRecorder stopped");
        } catch (error) {
          console.error("Failed to stop recording:", error);
        }
      } else {
        // Log photo taken if not recording
        console.log("Photo taken");
        handlePhotoCapture();
      }
    }
  };

  const handleAgree = () => {
    setShowPrivacyPopup(false);
    setTimeout(() => {
      setVantaFadeOut(true); // Trigger fade-out for Vanta background
      document.body.classList.add("fade-in-elements"); // Add class to trigger fade-in for hidden elements
    }, 500); // Delay of 500ms
  };

  return (
    <div
      className={`camera-wrapper ${showPrivacyPopup ? "hide-elements" : ""}`}
    >
      <Preloader />
      <VantaBackground className={vantaFadeOut ? "fade-out" : ""} />
      <div className="camera-container">
        {showPrivacyPopup && <PrivacyPopup onAgree={handleAgree} />}
        <section className="canvas-section">
          <canvas ref={canvasRef}></canvas>
          <Navbar className={showPrivacyPopup ? "hidden" : ""} />
          <div
            className={`carousel-wrapper ${showPrivacyPopup ? "hidden" : ""}`}
          >
            {currentLenses.length > 0 && (
              <LensCarousel
                lenses={currentLenses}
                onLensChange={handleLensChange}
                onRecord={handleRecord}
                activeIndex={activeIndex}
                isRecording={isRecording}
                setActiveIndex={setActiveIndex}
                loadedLensIndex={loadedLensIndex}
                fadeOut={fadeOut} // Pass fadeOut prop
              />
            )}
          </div>
          <img
            src={Snaplogo}
            alt="Powered by Snap"
            className={`snap-logo ${showPrivacyPopup ? "hidden" : ""}`}
          />
        </section>
        {!hideUI && (
          <CameraControls
            flipCamera={flipCamera}
            isMicOn={isMicOn}
            setIsMicOn={setIsMicOn}
            sessionRef={sessionRef}
            mediaStreamRef={mediaStreamRef}
            className={`${fadeOut ? "fade-out" : ""} ${
              fadeIn ? "fade-in" : ""
            }`}
          />
        )}
        {previewPopup && (
          <div className="preview-popup">
            <div className="preview-popup-content polaroid-animation">
              {isVideoPreview ? (
                <video
                  key={previewPopup} // Force React to remount when URL changes
                  controls
                  autoPlay // Play automatically when loaded
                  style={{ width: "100%", borderRadius: "10px" }}
                >
                  <source src={previewPopup} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img src={previewPopup} alt="Preview" />
              )}
              <div className="preview-popup-buttons">
                <button onClick={downloadPreview}>
                  <Download size={20} />
                </button>
                <button onClick={sharePreview}>
                  <Share2 size={20} />
                </button>
                <button onClick={closePreviewPopup}>
                  <ArrowLeft size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Camera;
